import * as React from 'react';

import Slider from 'react-md/lib/Sliders';

import { FamilyDetails } from '../Calculation/types';
import { ExplanationText } from './ExplanationText';
import { NumericField } from './NumericField';
import { QuestionLabel } from './QuestionLabel';
import { Cell } from 'react-md/lib/Grids';
import { CurrencyField } from './CurrencyField';
import SelectionControlGroup from 'react-md/lib/SelectionControls/SelectionControlGroup';
import ReactAnimateHeight, { AnimateHeightProps } from 'react-animate-height';
import SelectField from 'react-md/lib/SelectFields';


interface State {
}

interface Props {
    familyDetails: FamilyDetails;
    onRecognisedActivityUpdated: (value: number) => void;
    onCombinedIncomeUpdated: (combinedTaxableIncome: number) => void;
    onKnowSubsidyDetailsUpdated: (knowSubsidyDetails: boolean) => void;
    onSubsidyPercentageUpdated: (subsidyPercentage: number | undefined) => void;
    onHoursEligableForSubsidyUpdated: (subsidizedHours: number | undefined) => void;
}

export class FamilyIncomeAndActivity extends React.Component<Props & React.HTMLAttributes<HTMLDivElement>, State> {
    private readonly animateOptions: Partial<AnimateHeightProps> = {
      animateOpacity: true,
      duration: 1000
    };
  
    public render() {
      return <div className={this.props.className}>
      <QuestionLabel labelText={<span>Have you been provided with a <em>subsidy percentage</em> &amp; number of <em>subsidised hours</em> by Centrelink?</span>}>
      <ExplanationText>If not, don't worry - please select "No" below. </ExplanationText>
        <SelectionControlGroup
              id="haveSubsidyDetailsAlready"
              name="haveSubsidyDetailsAlready"
              type="radio"
              value={`${this.props.familyDetails.knowSubsidyDetails}`}
              onChange={(newValue) => { this.props.onKnowSubsidyDetailsUpdated(newValue === "true"); }}
              inline={true}
              controls={[{label: "Yes", value: "true"}, {label: "No", value: "false"}]}
            />
        </QuestionLabel>
      
      {this._renderSubsidyDetailsInputs()}
      {this._renderIncomeAndActivityInputs()}

      </div>
    }

    private _renderIncomeAndActivityInputs() {
      const { familyDetails } = this.props;
      return <ReactAnimateHeight {...this.animateOptions} height={this.props.familyDetails.knowSubsidyDetails ? "0" : "auto"}>
      <QuestionLabel labelText={<span>What is your <em>combined</em> family income that you report to Centrelink, rounded to the nearest $1,000?</span>}>
        <Slider id="familyIncomeSlider" leftIcon={<span>$0</span>} value={familyDetails.combinedTaxableIncome} min={0} max={350000} step={1000} rightIcon={<span>$350,000+</span>} onChange={this.props.onCombinedIncomeUpdated}></Slider>
        <div style={{display: "flex", justifyContent: "center"}}><CurrencyField value={familyDetails.combinedTaxableIncome} id="familyIncomeInput" onChange={this.props.onCombinedIncomeUpdated} step={1000} clearOnFocus={true} min={0} max={350000}></CurrencyField></div>
      </QuestionLabel>
     
      <ExplanationText>Family income for Centrelink purposes includes reportable Fringe Benefits, superannuation contributions that are salary sacrificed, total net investment losses, Disability, Carers or Wifes Pensions, and less and Child Support you pay.</ExplanationText>
      
      <QuestionLabel labelText={<span>What are the hours of 'recognised activity' typically undertaken each <em>fortnight</em> by the parent who does the least?</span>}>
        <Cell>
          <NumericField id="recognisedActivityHoursInput" min={0} max={80} step={1} fullWidth={true} onChange={this.setRecognisedActivity} clearOnFocus={true} value={familyDetails.activityHoursPerFortnight} placeholder="enter a number (max. 80)"></NumericField>
          </Cell>
      </QuestionLabel>
      
      <ExplanationText>‘Recognised Activity’ includes work, study, training, looking for a job, paid and unpaid leave, travelling to and from work, lunch breaks, unpaid work in a family business, setting up a business, work experience and volunteering in the community. If your work is casual or irregular, you should report the maximum number of hours you expect in the next three months, rather than the average.Remember, activity is determined on the parent that is doing the least in the household.</ExplanationText>        
      </ReactAnimateHeight>
    }

    private _renderSubsidyDetailsInputs() {
      const { familyDetails } = this.props;

      let hoursEligableForSubsidyOptions = [
        0, 24, 36, 72, 100
      ].map(hours => ({ label: `${hours} subsidised hours`, value: hours }));

      /* 
        Notes:
          - The subsidy percentage must be one of (exacly) 100%, 95% or 0%, OR it must fall within the range of 85% to 20% (inclusive on both ends)
          - The option of "24 subsidised hours" is only valid when the subsidy percentage is (exactly) 85%
          
          We don't enforce either of those rules currently
      */

      let subsidyPercentage: number | undefined = familyDetails.subsidyPercentage === undefined ? undefined : familyDetails.subsidyPercentage * 100;

      return <ReactAnimateHeight {...this.animateOptions} height={familyDetails.knowSubsidyDetails ? "auto" : "0"}>
      <QuestionLabel labelText={<span>What is your subsidy percentage?</span>}>
      <ExplanationText>Enter the percentage as a number between 0-100. For example, if your subsidy percentage is 55.25% then enter 55.25 (not 0.5525)
      <ul><li>The subsidy percentage is specified with up to two decimal places</li><li>There are specific ranges the subsidy percentage must fall within for it to be valid</li></ul>        
      </ExplanationText>
        <Cell>
          <NumericField id="subsidyPercentageInput" min={0} max={100} step={0.01} fullWidth={true} onChange={this.setSubsidyPercentage} clearOnFocus={false} value={subsidyPercentage} placeholder="enter a percentage"></NumericField>
          </Cell>
      </QuestionLabel>
    
      {/* IE11/Edge don't behave properly when SelectField is wrapped w/ QuestionLabel (select dispels immediately after clicking), so use the label "for" variant instead*/}
      <QuestionLabel labelText={<span>How many subsidised hours are you eligable for (per fortnight)?</span>} for="eligable-hours-select" />
      <SelectField className="md-cell" value={familyDetails.hoursEligableForSubsidyPerFortnight === undefined ? "" : familyDetails.hoursEligableForSubsidyPerFortnight} id="eligable-hours-select" menuItems={hoursEligableForSubsidyOptions} placeholder="Select number of subsidised hours" onChange={this.setSubsidizedHours} />
      </ReactAnimateHeight>;
    }
    
    private setRecognisedActivity = (recognisedActivityHours: number) => {
      this.props.onRecognisedActivityUpdated(recognisedActivityHours);
    }
    
    private setSubsidyPercentage = (subsidyPercentage: number) => {
      this.props.onSubsidyPercentageUpdated(subsidyPercentage / 100);
    }

    private setSubsidizedHours = (value: string) => {
      let hours: number | undefined = parseInt(value);
      if (isNaN(hours)) {
        hours = undefined;
      }

      this.props.onHoursEligableForSubsidyUpdated(hours);
    }
  }